import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import { TaskFilterInputObject } from 'graphql-common';
import Chip, { ChipSizes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Map from '@lib/components/Map/Map';
import Collapse from '@lib/components/Collapse/Collapse';
import { MapSize } from '@lib/components/Map/enums';
import { FormRef } from '@lib/components/ReactHookForm/types';
import TabBar from '@lib/components/TabBar/TabBar';
import Typography from '@lib/components/Typography/Typography';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import {
  DASHBOARD_MONITORING_FILTER_TYPE,
  SECTOR_MONITORING_MAP_VIEW_PARAMS,
  SITE_MONITORING_MAP_VIEW_PARAMS,
} from '@lib/enums/localStorageKeys';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { Values } from '@lib/interfaces/form';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import SiteMapPopupContentAsyncWrap from 'components/SiteMapPopupContent/SiteMapPopupContentAsyncWrap';
import SectorMapPopupContentAsyncWrap from 'components/SectorMapPopupContent/SectorMapPopupContentAsyncWrap';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import ChipIconCircle from '@lib/components/Chip/ChipIconCircle';
import { Colors } from '@lib/enums/colors';
import { MapParams } from '@lib/components/Map/types';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { captureException } from '@sentry/react';
import { handleCenterMap } from '@lib/utils/mapUtils';
import { LatLngExpression } from 'leaflet';
import getMapData from '../utils/getMapData';
import getLegendData from '../utils/getLegendData';
import useMonitoringMapActions from '../utils/useMonitoringMapActions';
import useMonitoringMapQueries from '../utils/useMonitoringMapQueries';
import LegendModalForm from '../LegendFormModal/LegendModalForm';
import getMonitoringEntity from '../utils/getMonitoringEntity';
import getStatusKind from '../utils/getStatusKind';
import getMapTabs from '../utils/getMapTabs';
import { MonitoringEntity, MonitoringType } from '../enums';
import { PerformanceLegend } from '../types';
import styles from './MonitoringMap.module.scss';

const modals = [Modals.LegendFormModal];

export default function MonitoringMap({
  taskFilters,
  filterParams,
  onUpdateFilterParams,
}: {
  taskFilters: TaskFilterInputObject[];
  filterParams: Values;
  onUpdateFilterParams: (values: Values) => void;
}) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const mapRef = useRef<L.Map>(null);
  const useModalManagementResult = useModalManagement(modals);

  const monitoringEntity = getMonitoringEntity({ filterParams });
  const statusKind = getStatusKind({ filterParams });

  const tabs = getMapTabs({ statusKind });

  const [monitoringType, setMonitoringType] = useLocalStorage(
    DASHBOARD_MONITORING_FILTER_TYPE,
    MonitoringType.Anomaly,
  );
  const [showSitesOnSectors, setShowSitesOnSectors] = useState(false);

  const [siteMapParams, setSiteMapParams] = useLocalStorage<MapParams>(
    SITE_MONITORING_MAP_VIEW_PARAMS,
    null,
  );
  const [sectorMapParams, setSectorMapParams] = useLocalStorage<MapParams>(
    SECTOR_MONITORING_MAP_VIEW_PARAMS,
    null,
  );
  const mapParams =
    monitoringEntity === MonitoringEntity.Site
      ? siteMapParams
      : sectorMapParams;
  const setMapParams =
    monitoringEntity === MonitoringEntity.Site
      ? setSiteMapParams
      : setSectorMapParams;

  // Legends
  const legendData: PerformanceLegend = useMemo(
    () => getLegendData({ filterParams, monitoringType, monitoringEntity }),
    [filterParams, monitoringEntity, monitoringType],
  );

  // Actions
  const {
    onLegendFormModalClose,
    onLegendFormModalOpen,
    onLegendUpdate,
    onTabChange,
    onToggleShowSitesOnSectors,
  } = useMonitoringMapActions({
    monitoringEntity,
    monitoringType,
    statusKind,
    onUpdateFilterParams,
    setMonitoringType,
    setShowSitesOnSectors,
    useModalManagementResult,
  });

  const {
    mapData,
    sectorLocationCenterLoading,
    sectorLocationCenterQueryHookResult,
    sectorLocationsQueryHookResult,
    sectorsPerformanceMetricsHookResult,
    siteLocationCenterLoading,
    siteLocationCenterQueryHookResult,
    siteLocationsQueryHookResult,
    sitesPerformanceMetricsHookResult,
  } = useMonitoringMapQueries({
    mapParams,
    monitoringEntity,
    showSitesOnSectors,
    taskFilters,
    statusKind,
  });

  const {
    loading: sitesPerformanceMetricsLoading,
    error: sitesPerformanceMetricsError,
  } = sitesPerformanceMetricsHookResult;

  const {
    loading: sectorsPerformanceMetricsLoading,
    error: sectorsPerformanceMetricsError,
  } = sectorsPerformanceMetricsHookResult;

  const { data: sitesPerformanceMetrics } = sitesPerformanceMetricsHookResult;
  const { data: sectorsPerformanceMetrics } =
    sectorsPerformanceMetricsHookResult;

  const onCenterMap = () => {
    let refetch: (() => Promise<any>) | undefined;
    if (monitoringEntity === MonitoringEntity.Site) {
      refetch = siteLocationCenterQueryHookResult.refetch;
    } else if (monitoringEntity === MonitoringEntity.Sector) {
      refetch = sectorLocationCenterQueryHookResult.refetch;
    }
    if (refetch) {
      refetch().then((res) => {
        handleCenterMap({ res, mapRef });
      });
    }
  };

  let metricsData;
  if (monitoringEntity === MonitoringEntity.Site) {
    metricsData = sitesPerformanceMetrics;
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    metricsData = showSitesOnSectors
      ? [
          ...(Array.isArray(sitesPerformanceMetrics)
            ? sitesPerformanceMetrics
            : []),
          ...(Array.isArray(sectorsPerformanceMetrics)
            ? sectorsPerformanceMetrics
            : []),
        ]
      : sectorsPerformanceMetrics;
  }

  const firstLoading =
    siteLocationsQueryHookResult.firstLoading ||
    sectorLocationsQueryHookResult.firstLoading ||
    siteLocationCenterQueryHookResult.firstLoading ||
    sectorLocationCenterQueryHookResult.firstLoading;

  const isLoading =
    firstLoading ||
    sitesPerformanceMetricsLoading ||
    sectorsPerformanceMetricsLoading;

  const siteLocationCenter = siteLocationCenterQueryHookResult.data;
  const sectorLocationCenter = sectorLocationCenterQueryHookResult.data;

  let newCenter: LatLngExpression | null = null;
  if (
    monitoringEntity === MonitoringEntity.Site &&
    siteLocationCenter?.lon &&
    siteLocationCenter?.lat
  ) {
    newCenter = { lng: siteLocationCenter.lon, lat: siteLocationCenter.lat };
  } else if (
    monitoringEntity === MonitoringEntity.Sector &&
    sectorLocationCenter?.lon &&
    sectorLocationCenter?.lat
  ) {
    newCenter = {
      lng: sectorLocationCenter.lon,
      lat: sectorLocationCenter.lat,
    };
  }

  const mergedMapData = useMemo(() => {
    if (isLoading) return [];
    if (!legendData || !mapData) return mapData;
    try {
      return getMapData(mapData, metricsData, legendData);
    } catch (e) {
      captureException(e);
      return mapData;
    }
  }, [legendData, mapData, metricsData, isLoading]);

  const legendItems = legendData?.items;
  const legendItemsArray = legendItems
    ? [...legendItems].sort((a, b) => a.from - b.from)
    : [];
  const legendOverlay = (
    <WhiteBox
      className={styles.legendOverlay}
      verticalPaddings="custom"
      horizontalPaddings="custom"
    >
      <div className={styles.legendHeader}>
        {monitoringType === MonitoringType.Anomaly && (
          <Typography variant="caption">{t('anomalies')}</Typography>
        )}
        {monitoringType === MonitoringType.Performance && (
          <Typography variant="caption">{t('performance')}</Typography>
        )}
        <IconButton
          icon="settings"
          onClick={onLegendFormModalOpen}
          rounded
          symbolsOutlined
          type={IconButtonTypes.SecondaryFilled}
        />
      </div>
      {legendData && (
        <div className={styles.legendBody}>
          <Collapse minHeight={185}>
            <div>
              {legendItemsArray?.map(({ color, from, to }) => (
                <div className={styles.legendItem} key={from}>
                  <Chip
                    type={colorToChipType(color)}
                    leftIcon="circle"
                    size={ChipSizes.smallLeftIconOnly}
                    leftIconComponent={
                      <ChipIconCircle
                        color={color as Colors}
                        dashed={monitoringEntity === MonitoringEntity.Sector}
                      />
                    }
                  />
                  <Typography variant="caption">
                    {from}
                    {_isNil(to) && '+'}
                    {!_isNil(to) && to > 0 && `-${to}`}
                    {monitoringType === MonitoringType.Performance && '%'}
                  </Typography>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      )}
      {monitoringEntity === MonitoringEntity.Sector && (
        <div className={styles.legendFooter}>
          <CheckBoxSingleBool
            name="show-sites"
            value={showSitesOnSectors}
            options={[{ label: 'checked', value: 'checked' }]}
            onChange={onToggleShowSitesOnSectors}
            label={t('show-sites')}
            CheckBoxComponent={CheckBoxAsToggle}
            labelTypographyProps={{ variant: 'caption', strong: false }}
            size={CheckboxSizes.Small}
          />
        </div>
      )}
    </WhiteBox>
  );

  const popupContentRender = (renderProps) => {
    const entityId = _get(renderProps, ['properties', 'sourceables', 0, 'id']);
    const entityType = _get(renderProps, [
      'properties',
      'sourceables',
      0,
      'type',
    ]);

    if (entityType === 'SITE') {
      return (
        <SiteMapPopupContentAsyncWrap siteId={entityId} {...renderProps} />
      );
    }
    if (entityType === 'SECTOR') {
      return (
        <SectorMapPopupContentAsyncWrap sectorId={entityId} {...renderProps} />
      );
    }

    return undefined;
  };

  const hasError = !!(
    sitesPerformanceMetricsError ||
    sectorsPerformanceMetricsError ||
    siteLocationsQueryHookResult.error ||
    sectorLocationsQueryHookResult.error
  );

  return (
    <>
      <div>
        <DashboardTitleHeader
          title={t(
            monitoringEntity === MonitoringEntity.Site
              ? 'sites-map'
              : 'sectors-map',
          )}
          titleVariant="h3"
          bottomSpace="middle"
        />
        {hasError ? (
          <EmptyState
            icon={IconName.Cancel}
            text={t('refresh-or-retry-later')}
            title={t('failed-to-load')}
          />
        ) : (
          <>
            <TabBar
              items={tabs}
              active={monitoringType}
              onActiveChange={onTabChange}
            />
            {siteLocationCenterLoading || sectorLocationCenterLoading ? (
              <CircleLoader />
            ) : (
              <CircleLoaderWrapper isLoading={isLoading}>
                <Map
                  defaultCenter={newCenter}
                  disableAutoFit
                  mapParams={mapParams}
                  mapRef={mapRef}
                  onCenterMap={onCenterMap}
                  PopupContent={popupContentRender}
                  setMapParams={setMapParams}
                  size={MapSize.Large}
                  topRightOverlay={legendOverlay}
                  value={mergedMapData}
                />
              </CircleLoaderWrapper>
            )}
          </>
        )}
      </div>
      <LegendModalForm
        isOpen={useModalManagementResult.isModalOpened(Modals.LegendFormModal)}
        onClose={onLegendFormModalClose}
        onCancel={onLegendFormModalClose}
        onSubmitCallback={onLegendUpdate}
        formRef={formRef}
        legendData={legendData}
        monitoringEntity={monitoringEntity}
        monitoringType={monitoringType}
        statusKind={statusKind}
      />
    </>
  );
}
